/* ============================================================== */
/*                                                                */
/*   queueDomainV4  ·  domain ของ Patient Queue (HIS V4)             */
/*                                                                */
/*   เป็นไฟล์ pure (ไม่มี issara-sdk / ไม่มี sequence)                */
/*   - แถวคิว QueueEncounter + ขั้น (stage) ของผู้ป่วย                */
/*   - คำสั่งแพทย์ (doctor orders) -> หมวดหมู่ + สถานะ                 */
/*   - รูปแบบเวลาในตาราง + map payload API -> แถวคิว                 */
/*   - filter ของ encounter-patient-optimized                        */
/*                                                                */
/*   แยกมาจาก common/sequence/PatientListV4.ts เพื่อให้                 */
/*   CardPatientListV4 / CardOrdersV4 / MainHISV4 / test import ได้     */
/*   โดยไม่ต้องลาก SDK เข้ามาใน dependency graph                      */
/*                                                                */
/* ============================================================== */

import moment from "moment";

// Utils
import { formatDateToStringBe } from "react-lib/utils/dateUtils";

// เมนูของ Patient Panel ที่เปิดเมื่อเข้า Encounter (type only ไม่มี cycle ตอน compile)
import type { PatientMenuKey } from "../patient/PatientPanelV4";

/* ============================================================== */
/*   SECTION 1 · QUEUE DOMAIN · TYPE ของแถวคิว                     */
/* ============================================================== */

/** รูปแบบ appoint_time ของ encounter-patient-optimized */
export const APPOINT_FORMAT = "DD/MM/YY [[]HH:mm[]]";

/** แถวคิวใช้ชื่อ field ตาม encounter-patient-optimized */
export type QueueEncounter = {
  id: number;
  number: string;
  hn: string;
  patient_id?: number;
  division?: number;
  patient_name: string;
  triage_level: string;
  is_appointment: boolean;
  created: string;
  created_utc: string;
  appoint_time: string;
  doctor_name: string;
  status: string;
  status_name: string;
  doctor_orders: { specific_type: string; order_status: string }[];
  has_info?: boolean;
  has_warning?: boolean;
  /** id ของเวชระเบียน (EMR) — Communication Note (DPO sensitive_note) ผูกกับ emr นี้ (#86727) */
  medical_record?: number;
};

/* ============================================================== */
/*   SECTION 2 · QUEUE DOMAIN · ขั้น (stage) ของผู้ป่วยในคิว        */
/* ============================================================== */

export type QueueStageKey =
  "wait-screen" | "wait-doctor" | "in-doctor" | "wait-lab" | "wait-pharmacy" | "wait-pay" | "done";

export type QueueStage = {
  key: QueueStageKey;
  label: string;
  action: string;
  /** สถานะ encounter ที่อยู่ในขั้นนี้ */
  statuses: string[];
  /** เมนูที่เปิดเมื่อเข้า Encounter */
  menu: PatientMenuKey;
  /** ปุ่มท้ายแถวเปิด Encounter */
  opensEncounter: boolean;
};

export const QUEUE_STAGES: QueueStage[] = [
  {
    key: "wait-screen",
    label: "รอคัดกรอง",
    action: "คัดกรอง",
    statuses: ["WAIT_TRIAGE", "ARRIVING", "ARRIVED"],
    menu: "screen",
    opensEncounter: true,
  },
  {
    key: "wait-doctor",
    label: "รอพบแพทย์",
    action: "เรียกเข้าตรวจ",
    statuses: ["SCREENED", "IN_QUEUE", "RECEIVE_RESULT"],
    menu: "doctor",
    opensEncounter: true,
  },
  {
    key: "in-doctor",
    label: "กำลังพบแพทย์",
    action: "ส่งต่อ",
    statuses: ["IN_EXAM"],
    menu: "doctor",
    opensEncounter: false,
  },
  {
    key: "wait-lab",
    label: "รอเก็บสิ่งส่งตรวจ",
    action: "เก็บสิ่งส่งตรวจ",
    statuses: ["WAIT_RESULT"],
    menu: "doctor",
    opensEncounter: false,
  },
  {
    key: "wait-pharmacy",
    label: "รอจ่ายยา",
    action: "จัดยา / จ่ายยา",
    statuses: ["WAIT_DISPENSE"],
    /* เปิด panel ที่เมนู "คำสั่ง" (ยา/เวชภัณฑ์ พร้อมสถานะจ่าย) เป็นหน้าที่ทำงานใน V4 */
    menu: "orders",
    opensEncounter: true,
  },
  {
    key: "wait-pay",
    label: "รอชำระเงิน",
    action: "รับชำระเงิน",
    statuses: ["WAIT_PAY"],
    menu: "billing",
    opensEncounter: true,
  },
  {
    key: "done",
    label: "เสร็จสิ้น",
    action: "เสร็จสิ้นแล้ว",
    statuses: ["DISCHARGED"],
    menu: "doctor",
    opensEncounter: false,
  },
];

/** หาขั้นของผู้ป่วยจาก status ของ encounter */
export function stageOf(encounter: unknown): QueueStage | undefined {
  const status = (encounter as { status?: unknown } | null)?.status;
  return QUEUE_STAGES.find(
    (stage) => typeof status === "string" && stage.statuses.includes(status),
  );
}

/** หาขั้นจาก key (ใช้ตอนเปลี่ยนสถานะไปขั้นถัดไป) */
export const stageByKey = (key: QueueStageKey): QueueStage =>
  QUEUE_STAGES.find((stage) => stage.key === key) ?? QUEUE_STAGES[QUEUE_STAGES.length - 1];

/**
 * OPD action ของ EncounterActionOnly.patch (apis/core/encounter/update-status/) ที่ใช้
 * เลื่อน encounter เข้าแต่ละขั้นของคิว — ตรวจแล้วออกจากการตรวจ (IN_EXAM) -> ขั้นถัดไป
 * ตามคำสั่งที่เหลือ (ดู nextStageAfterExam) ไม่ต้องข้ามหน้า แค่เปลี่ยนสถานะ
 */
export const STAGE_TRANSITION_ACTION: Record<QueueStageKey, string> = {
  "wait-screen": "OPD_SCREEN",
  "wait-doctor": "OPD_QUEUE_EXAM",
  "in-doctor": "OPD_CHECK_IN",
  "wait-lab": "OPD_SEND_LAB",
  "wait-pharmacy": "OPD_DISPENSE",
  "wait-pay": "OPD_CHECK_OUT",
  done: "OPD_DISCHARGE",
};

export type QueueTransitionDecision = {
  /** OPD action ที่ patch ให้ backend เลื่อนสถานะ */
  action: string;
  toStage: QueueStageKey;
  stage: QueueStage;
};

/**
 * ขั้นถัดไปของปุ่ม "ส่งต่อ" (กำลังพบแพทย์ -> ขั้นถัดไปตามคำสั่งที่มี) ตาม ticket #86606:
 * - มีคำสั่ง lab/imaging ค้างเก็บสิ่งส่งตรวจ -> "รอเก็บสิ่งส่งตรวจ"
 * - ไม่มีแลป แต่มียา/เวชภัณฑ์ค้างจ่าย          -> "รอจ่ายยา"
 * - ไม่มีอะไรค้าง (หัตถการทำเสร็จในขั้นพบแพทย์แล้ว) -> "รอชำระเงิน"
 */
export const nextStageAfterExam = (
  /* API ตอบ doctor_orders ได้แบบ field optional -> ห้ามบังคับให้ caller cast */
  orders: { specific_type?: string | null; order_status?: string | null }[] = [],
): QueueTransitionDecision => {
  const kinds = orders
    .map((order) => orderKindOf(order?.specific_type || ""))
    .filter((kind): kind is OrderKind => !!kind);
  const has = (...wanted: OrderKind[]) => kinds.some((kind) => wanted.includes(kind));

  const toStage: QueueStageKey = has("lab", "imaging")
    ? "wait-lab"
    : has("drug", "supply")
      ? "wait-pharmacy"
      : "wait-pay";

  return { action: STAGE_TRANSITION_ACTION[toStage], toStage, stage: stageByKey(toStage) };
};

/* ============================================================== */
/*   SECTION 3 · QUEUE DOMAIN · คำสั่งแพทย์ (doctor orders)         */
/* ============================================================== */

export type OrderKind = "lab" | "imaging" | "treatment" | "drug" | "supply";
export type OrderState = "pending" | "progress" | "complete";

export const ORDER_KIND_LABELS: Record<OrderKind, string> = {
  lab: "แลป",
  imaging: "Imaging",
  treatment: "หัตถการ",
  drug: "ยา",
  supply: "เวชภัณฑ์",
};

export const ORDER_STATE_LABELS: Record<OrderState, string> = {
  pending: "Pending",
  progress: "In Progress",
  complete: "Complete",
};

/** ขั้นที่คำสั่งแต่ละประเภทสังกัด */
const ORDER_STAGE: Record<OrderKind, QueueStageKey> = {
  lab: "wait-lab",
  imaging: "wait-lab",
  treatment: "in-doctor",
  drug: "wait-pharmacy",
  supply: "wait-pharmacy",
};

const LAB_TYPES = [
  "centrallaborder",
  "cytopathologyorder",
  "surgicalpathologyorder",
  "frozensectionorder",
  "bloodorderitem",
];

export const orderKindOf = (specificType: string): OrderKind | undefined => {
  if (LAB_TYPES.includes(specificType)) return "lab";
  if (specificType === "imagingorder") return "imaging";
  if (specificType === "treatmentorder") return "treatment";
  if (specificType.startsWith("supply")) return "supply";
  if (specificType.startsWith("drug")) return "drug";
  return undefined;
};

/** ขั้นของคำสั่งยังมาไม่ถึง = pending, อยู่ในขั้น = progress, ผ่านแล้ว = complete */
const orderStateOf = (kind: OrderKind, stageKey?: QueueStageKey): OrderState => {
  const keys = QUEUE_STAGES.map((stage) => stage.key);
  const current = stageKey ? keys.indexOf(stageKey) : -1;
  const target = keys.indexOf(ORDER_STAGE[kind]);
  if (current < target) return "pending";
  return current === target ? "progress" : "complete";
};

/** รวม doctor_orders ตามประเภท สถานะคิดจากขั้นของผู้ป่วย */
export function orderGroups(
  orders: QueueEncounter["doctor_orders"] = [],
  stageKey?: QueueStageKey,
) {
  const groups: { kind: OrderKind; state: OrderState; count: number }[] = [];
  for (const order of orders) {
    const kind = orderKindOf(order.specific_type || "");
    if (!kind) continue;
    const group = groups.find((item) => item.kind === kind);
    if (group) {
      group.count += 1;
    } else {
      groups.push({ kind, state: orderStateOf(kind, stageKey), count: 1 });
    }
  }
  return groups;
}

/* ============================================================== */
/*   SECTION 4 · QUEUE DOMAIN · รูปแบบเวลาในตารางคิว               */
/* ============================================================== */

/** created_utc → "DD/MM/YY HH:mm" (พ.ศ.) */
export const createdText = (createdUtc: string) => {
  const time = moment(createdUtc);
  return time.isValid()
    ? `${time.format("DD/MM/")}${String(time.year() + 543).slice(2)} ${time.format("HH:mm")}`
    : "-";
};

/** "24/09/26 [09:00]" → "09:00" */
export const appointText = (appointTime: string) => {
  const time = moment(appointTime, APPOINT_FORMAT);
  return time.isValid() ? time.format("HH:mm") : "-";
};

/** เวลาที่รอมาแล้วจาก created_utc */
export function waitText(createdUtc: string) {
  const minutes = Math.floor(moment().diff(moment(createdUtc), "minutes"));
  if (!createdUtc || Number.isNaN(minutes) || minutes < 0) return "-";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} นาที`;
  return rest === 0 ? `${hours} ชม.` : `${hours} ชม. ${rest} นาที`;
}

/* ============================================================== */
/*   SECTION 5 · QUEUE DOMAIN · map แถว API -> แถวคิวของตาราง      */
/* ============================================================== */

/** แถว Encounter ดิบจาก encounter-patient-optimized (EncounterPatientSerializer) */
export type RawQueueEncounter = Record<string, any>;

/** แปลงแถวจาก API ให้เป็น QueueEncounter ของ CardPatientListV4 */
export const toQueueEncounter = (item: RawQueueEncounter, index = 0): QueueEncounter => {
  const status = String(item?.status ?? "");
  const stage = stageOf({ status });
  const appointTime = typeof item?.appoint_time === "string" ? item.appoint_time : "";

  return {
    id: Number(item?.id ?? item?.pk ?? index),
    number: String(item?.number ?? item?.full_number ?? item?.encounter ?? item?.id ?? ""),
    hn: String(item?.hn ?? item?.patient_hn ?? item?.patient?.hn ?? item?.patient__hn ?? ""),
    patient_id: item?.patient_id ?? (typeof item?.patient === "number" ? item.patient : undefined),
    division: item?.division,
    patient_name:
      item?.patient_name ??
      [item?.first_name, item?.last_name].filter(Boolean).join(" ") ??
      item?.name ??
      "",
    triage_level: String(item?.triage_level ?? item?.triage ?? ""),
    is_appointment: Boolean(item?.is_appointment ?? !!appointTime),
    created: String(item?.created ?? ""),
    created_utc: String(item?.created_utc ?? item?.created ?? ""),
    appoint_time: appointTime,
    doctor_name: String(item?.doctor_name ?? item?.doctor ?? ""),
    status,
    status_name: String(item?.status_name ?? stage?.label ?? status),
    doctor_orders: Array.isArray(item?.doctor_orders) ? item?.doctor_orders : [],
    has_info: item?.has_info,
    has_warning: item?.has_warning,
    medical_record: item?.medical_record ?? item?.emr,
  };
};

/** #86602: เวลานับจาก created_utc เพื่อเรียง "เวลามา" ใหม่สุดก่อน */
const createdMs = (createdUtc: string): number => {
  const time = moment(createdUtc);
  return time.isValid() ? time.valueOf() : 0;
};

/**
 * items จาก API -> แถวคิวสำหรับตาราง (รองรับ {items}, {results}, {data}, array ตรง)
 * #86602: เรียง "เวลามา" ใหม่สุดอยู่บนเสมอ — sort ในโค้ด ไม่พึ่งลำดับที่ backend ส่งมา
 */
export const toQueueRows = (response: any): QueueEncounter[] => {
  const items = Array.isArray(response)
    ? response
    : response?.items ?? response?.results ?? response?.data;
  const rows: RawQueueEncounter[] = Array.isArray(items) ? items : [];
  return rows
    .map(toQueueEncounter)
    .sort((a, b) => createdMs(b.created_utc) - createdMs(a.created_utc));
};

/* ============================================================== */
/*   SECTION 6 · FILTER ของ encounter-patient-optimized            */
/* ============================================================== */

/** filter ของ /apis/core/encounter-patient-optimized/ (from_date/to_date เป็น DD/MM/YYYY พ.ศ.) */
export type FilterType = {
  from_date?: string | null;
  to_date?: string | null;
  doctor?: string | number | null;
  division?: string | number | null;
  patient__hn?: string | null;
  patient_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

/** filter ที่ UI ส่งมา (มีชื่อสั้นที่ SearchBox ใช้ + วันที่แบบอื่น) */
export type UiFilterType = Partial<FilterType> & {
  hn?: string | null;
  name?: string | null;
  date?: string | null;
};

/* ============================================================== */
/*   SECTION 6 · SEQUENCE HELPERS · แปลง filter จาก UI เป็น params   */
/* ============================================================== */

const todayBE = () => formatDateToStringBe(moment());

/**
 * แปลงวันที่เป็น "DD/MM/YYYY (พ.ศ.)"
 * API encounter-patient-optimized รับวันที่เป็นปี พ.ศ. เช่น 28/10/2569
 * รองรับ input: DD/MM/YYYY (พ.ศ.), DD/MM/YYYY (ค.ศ.) และ YYYY-MM-DD (ค.ศ.)
 */
export const ToBEDate = ({ date, fallback }: { date?: string | null; fallback?: string }) => {
  if (!date) return fallback ?? todayBE();

  const value = `${date}`.trim();

  // YYYY-MM-DD (ค.ศ.) -> DD/MM/YYYY (พ.ศ.)
  const ad = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  if (ad) {
    const day = moment({
      year: parseInt(ad[1], 10),
      month: parseInt(ad[2], 10) - 1,
      day: parseInt(ad[3], 10),
    });
    if (day.isValid()) return day.clone().add(543, "years").format("DD/MM/YYYY");
  }

  // DD/MM/YYYY (รับทั้ง พ.ศ. และ ค.ศ.) -> พ.ศ.
  const be = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (be) {
    const year = parseInt(be[3], 10);
    const day = moment({
      year: year < 2500 ? year + 543 : year,
      month: parseInt(be[2], 10) - 1,
      day: parseInt(be[1], 10),
    });
    if (day.isValid()) return day.format("DD/MM/YYYY");
  }

  return fallback ?? todayBE();
};

/**
 * normalize filter จาก UI (HN, ชื่อ/นามสกุล, หน่วยตรวจ, แพทย์, วันที่ พ.ศ.)
 * ให้เป็น params ของ /apis/core/encounter-patient-optimized/
 * from_date / to_date เป็น "DD/MM/YYYY (พ.ศ.)"
 */
export const NormalizeFilter = ({
  filters,
}: {
  filters: FilterType & {
    hn?: string | null;
    name?: string | null;
    date?: string | null;
  };
}) => {
  const name = filters.patient_name ?? filters.name ?? "";
  const [firstName, ...rest] = `${name}`.trim().split(/\s+/);
  const lastName = rest.join(" ");

  return {
    from_date: ToBEDate({ date: filters.from_date ?? filters.date }),
    to_date: ToBEDate({ date: filters.to_date ?? filters.date }),
    doctor: filters.doctor ?? "",
    division: filters.division ?? "",
    patient__hn: filters.patient__hn ?? filters.hn ?? "",
    patient_name: name,
    first_name: lastName ? firstName : "",
    last_name: lastName || "",
  };
};
